import { useState, useEffect } from 'react';
import ProposalDialog from '../../components/proposal/ProposalDialog';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout, TierBadge } from '../../components/layout/DashboardLayout';
import RecentActivity from '../../components/RecentActivity';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import {
  Users,
  Wallet,
  TrendingUp,
  IndianRupee,
  Plus,
  Download,
  Phone,
  ArrowUpRight,
  Tag,
  Clock,
  AlertCircle,
  FileText,
  CheckCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const PartnerDashboard = () => {
  const { user, getAuthHeaders, API_URL } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    fetchProposals();
    fetchStats();
  }, []);

  /** Send the resume link again. The endpoint answers identically whether or not it found
   *  an unfinished application, so there is nothing to branch on here. */
  const resendResumeLink = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resume/resend`, { email: user?.email });
      toast.success('Sent. Check your inbox for the link.');
    } catch {
      toast.error('Could not send that just now. Please try again in a minute.');
    } finally {
      setResending(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partner/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  /** A proposal sent to this partner must be findable after logging in. The token link works
      from an email, but a partner who signs in and cannot see their own offer will ask a human. */
  const fetchProposals = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/partner/proposals`, { headers: getAuthHeaders() });
      setProposals(res.data || []);
    } catch (error) {
      // A dashboard is still useful without this; failing loudly here helps nobody.
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ef785b]"></div>
        </div>
      </DashboardLayout>
    );
  }

  const openProposal = proposals.find((p) => p.status === 'sent' || p.status === 'viewed');
  const acceptedProposal = proposals.find((p) => p.status === 'accepted');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-dashboard">
        {/* An open proposal outranks everything else on this page — it is the one thing the
            partner has to act on, and it is the step that unblocks their approval. */}
        {openProposal && (
          <div className="flex items-start justify-between gap-4 p-4 bg-[#1A1A1A] rounded-xl" data-testid="proposal-banner">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-white">Your proposal is ready</p>
                <p className="text-sm text-white/70 mt-1">
                  Rate card v{openProposal.rate_card_version} — the rates your partnership would
                  run on. Review it and accept when you're ready.
                </p>
              </div>
            </div>
            {/* A dialog, not a Link. Navigating to /proposal/:token leaves the portal for a
                page with no navigation on it, so a partner who accepted was stranded on a
                standalone document with no way back. The public route still exists for the
                emailed link; this is the same document, read without leaving. */}
            <Button size="sm" variant="secondary" data-testid="open-proposal"
                    onClick={() => setProposalOpen(true)}>
              Open it
            </Button>
            <ProposalDialog
              token={openProposal.token}
              open={proposalOpen}
              onOpenChange={setProposalOpen}
              // The banner above is driven by proposal status, so it has to be re-read or an
              // accepted proposal keeps offering itself.
              onAccepted={() => { fetchProposals(); fetchStats(); }}
            />
          </div>
        )}
        {acceptedProposal && user?.status === 'pending' && (
          <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-800">Proposal accepted</p>
              <p className="text-sm text-green-700 mt-1">
                We're setting up your account now. You'll hear from us shortly.
              </p>
            </div>
          </div>
        )}

        {/* An unfinished application is the partner's own next step, and it outranks
            "we are reviewing you" — there is not yet enough on the record to review. The
            same link that was emailed is here too, because inboxes lose things. */}
        {user?.status === 'pending' && user?.application_complete === false && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <FileText className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Finish your application</p>
              <p className="text-sm text-amber-700 mt-1">
                A few questions about your agency and your book are still outstanding. They decide
                the demo we build for your call, so they are worth the two minutes.
              </p>
              <button
                type="button"
                onClick={resendResumeLink}
                disabled={resending}
                className="mt-3 text-sm font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950 disabled:opacity-60"
                data-testid="resend-resume-link"
              >
                {resending ? 'Sending...' : 'Email me the link again'}
              </button>
            </div>
          </div>
        )}

        {/* Pending Approval Banner */}
        {user?.status === 'pending' && user?.application_complete !== false && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Application Under Review</p>
              <p className="text-sm text-amber-700 mt-1">
                Your application is complete and with us. We come back to you either way, and your
                call is the next thing in the diary. In the meantime, feel free to explore the dashboard.
              </p>
            </div>
          </div>
        )}

        {user?.status === 'suspended' && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-800">Account Suspended</p>
              <p className="text-sm text-red-700 mt-1">
                Your partner account has been suspended. Please contact support at info@expedify.ai for assistance.
              </p>
            </div>
          </div>
        )}

        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">
              Welcome back, {user?.full_name?.split(' ')[0]} 👋
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <TierBadge tier={user?.tier} />
              {/* The tier IS what a partner is now. There is no second word describing them —
                  what they earn on any one client follows how that client is engaged. */}
              <span className="text-sm text-[#64748B]">Partner</span>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/leads">
              <Button className="bg-[#ef785b] hover:bg-[#D44017] text-white" data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-2" /> Add New Lead
              </Button>
            </Link>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Total Referred</p>
                  <p className="text-xl font-bold text-[#1A1A1A] stat-number" data-testid="total-clients">
                    {stats?.total_clients || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Active</p>
                  <p className="text-xl font-bold text-green-600 stat-number" data-testid="active-clients">
                    {stats?.active_clients || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Inactive</p>
                  <p className="text-xl font-bold text-red-600 stat-number" data-testid="inactive-clients">
                    {stats?.inactive_clients || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ef785b]/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-[#ef785b]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Total Earned</p>
                  <p className="text-xl font-bold text-[#1A1A1A] stat-number" data-testid="total-commission">
                    {formatCurrency(stats?.total_commission || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">This Month</p>
                  <p className="text-xl font-bold text-amber-600 stat-number" data-testid="month-commission">
                    {formatCurrency(stats?.this_month_commission || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Pending</p>
                  <p className="text-xl font-bold text-blue-600 stat-number" data-testid="pending-commission">
                    {formatCurrency(stats?.pending_commission || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts and Tier Progress */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Commission Chart */}
          <Card className="lg:col-span-2 bg-white border-[#E2E2DF]">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-[#1A1A1A]">
                Commission Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats?.monthly_data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E2DF" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      tickFormatter={(value) => {
                        const [year, month] = value.split('-');
                        return new Date(year, month - 1).toLocaleString('default', { month: 'short' });
                      }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Commission']}
                      labelFormatter={(value) => {
                        const [year, month] = value.split('-');
                        return new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="amount" 
                      stroke="#ef785b" 
                      strokeWidth={2}
                      dot={{ fill: '#ef785b', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: '#ef785b' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Tier Progress */}
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-[#1A1A1A]">
                Tier Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center p-4 bg-[#FAFAF8] rounded-xl">
                <p className="text-sm text-[#64748B] mb-2">Current Tier</p>
                <div className="inline-block">
                  <TierBadge tier={stats?.tier || 'silver'} />
                </div>
              </div>

              {stats?.quarterly_avg !== undefined && (
                <div className="text-center p-3 bg-[#FAFAF8] rounded-lg">
                  {/* The tier is measured over the trailing three months, not averaged over
                      every quarter since joining — so the label has to say which. */}
                  <p className="text-xs text-[#64748B] mb-1">Commission, last 3 months</p>
                  <p className="text-lg font-bold text-[#1A1A1A]">{formatCurrency(stats.quarterly_commission ?? stats.quarterly_avg ?? 0)}</p>
                </div>
              )}

              {stats?.next_tier && (
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#64748B]">Progress to {stats.next_tier}</span>
                    <span className="font-medium text-[#1A1A1A]">{Math.round(stats.tier_progress || 0)}%</span>
                  </div>
                  <Progress value={stats.tier_progress || 0} className="h-2 bg-[#E2E2DF]" />
                  <p className="text-xs text-[#64748B] mt-2">
                    {formatCurrency(stats.revenue_to_next || 0)} quarterly avg needed to unlock {stats.next_tier} tier
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-[#1A1A1A]">Tier Benefits</h4>
                <div className="space-y-1 text-sm text-[#64748B]">
                  <p className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ef785b]"></span>
                    {stats?.tier === 'silver' && 'Base commission rate and credit discount'}
                    {stats?.tier === 'gold' && 'Higher commission rate and credit discount'}
                    {stats?.tier === 'platinum' && 'Maximum commission and discount + co-branding'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-5 gap-4">
          <Link to="/leads">
            <Card className="bg-white border-[#E2E2DF] hover:border-[#ef785b]/30 transition-all cursor-pointer card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ef785b]/10 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-[#ef785b]" />
                </div>
                <div>
                  <p className="font-medium text-[#1A1A1A]">Add New Lead</p>
                  <p className="text-xs text-[#64748B]">Submit a referral</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#64748B] ml-auto" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/clients">
            <Card className="bg-white border-[#E2E2DF] hover:border-[#ef785b]/30 transition-all cursor-pointer card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-[#1A1A1A]">View Clients</p>
                  <p className="text-xs text-[#64748B]">Manage referrals</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#64748B] ml-auto" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/commissions">
            <Card className="bg-white border-[#E2E2DF] hover:border-[#ef785b]/30 transition-all cursor-pointer card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <Download className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-[#1A1A1A]">Commission Report</p>
                  <p className="text-xs text-[#64748B]">Download history</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#64748B] ml-auto" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/promo-codes">
            <Card className="bg-white border-[#E2E2DF] hover:border-[#ef785b]/30 transition-all cursor-pointer card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Tag className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-[#1A1A1A]">Promo Codes</p>
                  <p className="text-xs text-[#64748B]">Generate & manage</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#64748B] ml-auto" />
              </CardContent>
            </Card>
          </Link>

          <a href="mailto:support@expedify.in" className="block">
            <Card className="bg-white border-[#E2E2DF] hover:border-[#ef785b]/30 transition-all cursor-pointer card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-medium text-[#1A1A1A]">Contact Support</p>
                  <p className="text-xs text-[#64748B]">support@expedify.in</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#64748B] ml-auto" />
              </CardContent>
            </Card>
          </a>
        </div>

        {/* Recent Activity */}
        <RecentActivity />
      </div>
    </DashboardLayout>
  );
};

export default PartnerDashboard;
